/**
 * VideoEmbed
 *
 * Component to display video source info for video-sourced recipes.
 * Shows a thumbnail with play button overlay and link to open the original video.
 * Supports Instagram, Facebook, TikTok, and YouTube videos.
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { openExternalUrl } from '../../utils/openExternalUrl';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius, Colors } from '../../styles/theme';
import { RemoteImage } from '../shared/RemoteImage';

type VideoPlatform = 'instagram' | 'facebook' | 'youtube' | 'tiktok';

interface VideoEmbedProps {
  videoUrl: string;
  videoPlatform: VideoPlatform;
  videoThumbnail?: string;
}

/**
 * Get display name for video platform
 */
const getPlatformDisplayName = (platform: VideoPlatform): string => {
  const names: Record<VideoPlatform, string> = {
    instagram: 'Instagram',
    facebook: 'Facebook',
    youtube: 'YouTube',
    tiktok: 'TikTok',
  };
  return names[platform];
};

/**
 * Get icon for video platform
 */
const getPlatformIcon = (platform: VideoPlatform): string => {
  // Using Feather icons that best represent each platform
  const icons: Record<VideoPlatform, string> = {
    instagram: 'instagram',
    facebook: 'facebook',
    youtube: 'youtube',
    tiktok: 'music', // TikTok doesn't have a Feather icon, using music as alternative
  };
  return icons[platform];
};

export const VideoEmbed: React.FC<VideoEmbedProps> = ({
  videoUrl,
  videoPlatform,
  videoThumbnail,
}) => {
  const { t } = useTranslation();
  const handleOpenVideo = async () => {
    try {
      await openExternalUrl(videoUrl);
    } catch (error) {
      console.error('Error opening video URL:', error);
    }
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={styles.thumbnailContainer}
        onPress={handleOpenVideo}
        activeOpacity={0.9}
      >
        <RemoteImage
          uri={videoThumbnail}
          style={styles.thumbnail}
          resizeMode="cover"
          fallback={<View style={styles.placeholderThumbnail}>
              <Feather name="video" size={48} color="#CCC" />
            </View>}
        />
        <View style={styles.playOverlay}>
          <View style={styles.playButton}>
            <Feather name="play" size={32} color="white" />
          </View>
        </View>
        <View style={styles.platformBadge}>
          <Feather
            name={getPlatformIcon(videoPlatform) as any}
            size={14}
            color="white"
          />
          <Text style={styles.platformBadgeText}>
            {getPlatformDisplayName(videoPlatform)}
          </Text>
        </View>
      </TouchableOpacity>
      <TouchableOpacity style={styles.sourceButton} onPress={handleOpenVideo}>
        <Feather
          name={getPlatformIcon(videoPlatform) as any}
          size={16}
          color={Paprika.primary}
        />
        <Text style={styles.sourceButtonText}>{t('recipeDetail.watchOriginalVideo')}</Text>
        <Feather name="external-link" size={14} color={Paprika.primary} />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.lg,
  },
  thumbnailContainer: {
    aspectRatio: 16 / 9,
    borderRadius: PaprikaRadius.lg,
    overflow: 'hidden',
    backgroundColor: '#F5F5F5',
    position: 'relative',
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  placeholderThumbnail: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#EEE',
  },
  playOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  playButton: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: 4, // Offset for play icon visual centering
  },
  platformBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    gap: 6,
  },
  platformBadgeText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  sourceButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    marginTop: PaprikaSpacing.sm,
    backgroundColor: Paprika.primary + '10',
    borderRadius: PaprikaRadius.md,
  },
  sourceButtonText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.primary,
    fontWeight: '600',
  },
});

export default VideoEmbed;
